import { useEffect } from 'react'
import { useRouter } from 'next/router'

import { skipToken } from '@reduxjs/toolkit/query/react'

import { selectUserId } from '~/auth/slices/auth.slice'
import { useGetCurrentGradeQuery } from '~/curriculum/apis/curriculum.api'
import { useGetInCurriculumV2 } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import useGetGradeOverviewPageRoute from '~/dashboard/hooks/useGetGradeOverviewPageRoute'
import useGetGradePageRoute from '~/dashboard/hooks/useGetGradePageRoute'
import { useAppSelector } from '~/redux/hooks'

// The v1 curriculum route patterns (router.pathname) whose v2 counterpart is the
// grade overview page. `grade-country` has no countryCode param — the user's
// chosen curriculum country is substituted instead.
const V1_CURRICULUM_PAGE_PATHNAMES = [
  '/curriculum/[countryCode]/[grade]',
  '/curriculum/[countryCode]/[grade]/[subject]',
  '/curriculum/[countryCode]/[grade]/[subject]/mapping-quiz',
  '/curriculum/[countryCode]/[grade]/[subject]/[contentId]',
  '/curriculum/grade-country/[grade]',
]

// The v1 "pick a grade" surfaces (task scope point 3). A select-grade page has no
// grade yet, so its v2 counterpart is the v2 select-grade page — not a grade
// overview. The bootcamp pages map to the v2 curriculum select-grade; the
// onboarding pages map to the v2 onboarding select-grade.
const V1_BOOTCAMP_SELECT_GRADE_PATHNAMES = [
  '/bootcamp/select-grade/[countryCode]',
  '/bootcamp/select-grade/grade-country',
]
const V1_ONBOARDING_SELECT_GRADE_PATHNAMES = [
  '/onboarding/select-grade/[countryCode]',
  '/onboarding/select-grade/grade-country',
]

/**
 * Redirects Curriculum v2 users away from Curriculum v1 surfaces to their v2
 * counterpart. Call this only on the v1 surfaces it targets (not globally): the
 * v1 curriculum pages, the bootcamp/onboarding select-grade pages, and the
 * bootcamp/math-vault activity pages that can be opened in grade context
 * (`?showGrade=true`).
 *
 * The target grade/country come from the v1 curriculum URL params when present;
 * pages that don't carry them (showGrade, grade-country) fall back to the user's
 * current grade + chosen curriculum country.
 *
 * v1 pages are statically generated and the v2 flag is client-side only, so the
 * redirect must run in the browser via `router.replace` (no history entry).
 */
export default function useRedirectV2UserToGradeOverview(): void {
  const router = useRouter()
  const isCurriculumV2 = useGetInCurriculumV2()
  const userId = useAppSelector(selectUserId)

  const isV1CurriculumPage = V1_CURRICULUM_PAGE_PATHNAMES.includes(router.pathname)
  const isV1BootcampSelectGradePage = V1_BOOTCAMP_SELECT_GRADE_PATHNAMES.includes(router.pathname)
  const isV1OnboardingSelectGradePage = V1_ONBOARDING_SELECT_GRADE_PATHNAMES.includes(router.pathname)
  const isShowGrade = router.query.showGrade === 'true'

  const isV1SelectGradePage = isV1BootcampSelectGradePage || isV1OnboardingSelectGradePage

  const shouldRedirect = isCurriculumV2 && (isV1CurriculumPage || isV1SelectGradePage || isShowGrade)

  // The select-grade targets need no grade, so skip the current-grade fetch there.
  const needsCurrentGrade = shouldRedirect && !isV1SelectGradePage
  const { data: currentGrade } = useGetCurrentGradeQuery(!userId || !needsCurrentGrade ? skipToken : undefined)

  // v1 curriculum + select-grade URLs carry `grade-<countryCode>`; showGrade and
  // grade-country don't carry a countryCode.
  const urlCountryCode = (router.query.countryCode as string | undefined)?.split('-')[1]
  const urlGrade = router.query.grade as string | undefined

  const specificCountryCode = isV1CurriculumPage ? urlCountryCode : undefined
  const grade = urlGrade ? urlGrade : currentGrade?.grade ?? undefined

  const gradeOverviewUrl = useGetGradeOverviewPageRoute({ grade: grade ?? 1, specificCountryCode })
  // /onboarding/v2/<grade-countryCode>/select-grade for v2 users — country from the
  // v1 onboarding select-grade URL when present, else the chosen curriculum country.
  const v2OnboardingSelectGradeUrl = useGetGradePageRoute({ isOnboarding: true, specificCountryCode: urlCountryCode })
  // /curriculum/v2/<grade-countryCode>/select-grade for v2 users — country from the
  // v1 bootcamp select-grade URL when present, else the chosen curriculum country.
  const v2CurriculumSelectGradeUrl = useGetGradePageRoute({ specificCountryCode: urlCountryCode })

  useEffect(() => {
    // Wait for isReady so URL params are populated — otherwise a cached current
    // grade could win the race and redirect to the wrong grade overview.
    if (!router.isReady || !shouldRedirect) return
    if (isV1OnboardingSelectGradePage) {
      router.replace(v2OnboardingSelectGradeUrl)
      return
    }
    if (isV1BootcampSelectGradePage) {
      router.replace(v2CurriculumSelectGradeUrl)
      return
    }
    if (!grade) return
    router.replace(gradeOverviewUrl)
  }, [
    shouldRedirect,
    isV1OnboardingSelectGradePage,
    v2OnboardingSelectGradeUrl,
    isV1BootcampSelectGradePage,
    v2CurriculumSelectGradeUrl,
    grade,
    gradeOverviewUrl,
    router,
  ])
}
